import { defineStore } from 'pinia'

export const useCompareStore = defineStore('compare', {
  state: () => ({
    tools: [],
    maxTools: 4, // Maximum number of tools to compare
    widgetExpanded: true,
    isComparePage: false
  }),
  
  getters: {
    // Get list of tools in comparison
    compareTools: (state) => state.tools,
    
    // Get count of tools in comparison
    compareCount: (state) => state.tools.length,
    
    // Check if a specific tool is in the comparison
    isInCompare: (state) => (toolId) => {
      return state.tools.some(tool => tool.id === toolId)
    },
    
    // Check if the compare limit has been reached
    isCompareFull: (state) => state.tools.length >= state.maxTools,
    
    // Check if the compare widget should be visible
    shouldShowWidget: (state) => {
      // Show widget only if:
      // 1. There are tools to compare
      // 2. We're not on the compare page
      return state.tools.length > 0 && !state.isComparePage;
    },
    
    // Get tool IDs for API requests
    toolIds: (state) => state.tools.map(tool => tool.id)
  },
  
  actions: {
    // Add a tool to the comparison with more comprehensive data
    addTool(tool) {
      // Don't add if already in the comparison
      if (this.isInCompare(tool.id)) return false;
      
      // Don't add if at max capacity
      if (this.isCompareFull) return false;
      
      // Add more comprehensive data about the tool
      this.tools.push({
        id: tool.id,
        name: tool.name,
        slug: tool.slug,
        image: tool.media?.[0]?.original_url || '/storage/no-logo.png',
        short_description: tool.short_description,
        pricing_model: tool.pricing_model,
        platforms: tool.platforms ? tool.platforms.map(p => ({
          id: p.id,
          name: p.name
        })) : [],
        use_cases: tool.use_cases ? tool.use_cases.map(c => ({
          id: c.id,
          name: c.name,
          slug: c.slug
        })) : [],
        features: tool.features ? tool.features.map(f => ({
          id: f.id,
          name: f.name
        })) : []
      });
      
      // Save to localStorage
      this.persistToStorage();
      return true;
    },
    
    // Remove a tool from the comparison
    removeTool(toolId) {
      const index = this.tools.findIndex(tool => tool.id === toolId);
      if (index !== -1) {
        this.tools.splice(index, 1);
        this.persistToStorage();
        return true;
      }
      return false;
    },
    
    // Clear all tools from comparison
    clearTools() {
      this.tools = [];
      this.persistToStorage();
    },
    
    // Toggle widget expanded state
    toggleWidgetExpanded() {
      this.widgetExpanded = !this.widgetExpanded;
      this.persistToStorage();
    },
    
    // Collapse widget
    collapseWidget() {
      this.widgetExpanded = false;
      this.persistToStorage();
    },
    
    // Expand widget
    expandWidget() {
      this.widgetExpanded = true;
      this.persistToStorage();
    },
    
    // Set compare page status
    setComparePage(isOnComparePage) {
      this.isComparePage = isOnComparePage;
    },
    
    // Persist tools to localStorage
    persistToStorage() {
      localStorage.setItem('compareTools', JSON.stringify(this.tools));
      localStorage.setItem('compareWidgetExpanded', JSON.stringify(this.widgetExpanded));
    },
    
    // Load tools from localStorage
    loadFromStorage() {
      try {
        // Load tools
        const storedTools = localStorage.getItem('compareTools');
        if (storedTools) {
          this.tools = JSON.parse(storedTools);
        }
        
        // Load widget state
        const widgetExpanded = localStorage.getItem('compareWidgetExpanded');
        if (widgetExpanded !== null) {
          this.widgetExpanded = JSON.parse(widgetExpanded);
        }
      } catch (e) {
        // Reset to defaults on error
        this.tools = [];
        this.widgetExpanded = true;
      }
    },
    
    // Update tool data with fresh data from the API
    updateToolData(toolId, freshData) {
      const index = this.tools.findIndex(tool => tool.id === toolId);
      if (index !== -1) {
        // Update with fresh data while preserving the ID
        this.tools[index] = {
          ...freshData,
          id: toolId
        };
        this.persistToStorage();
      }
    }
  }
})